import { BugEntity } from "@archipad-models/models/BugEntity";
import { BugNoteEntity } from "@archipad-models/models/BugNoteEntity";
import { ProjectEntity } from "@archipad-models/models/ProjectEntity";
import { VisitEntity } from "@archipad-models/models/VisitEntity";
import _WorkflowUserManager, { WorkflowUserManager } from "@archipad/backend/project/workflowUserManager";
import { generateDeterministicId } from "@core/services/orm/idGenerator";
import { query } from "@core/services/orm/query";
import { WorkflowService } from "./workflow.service";
import { EquivBugState, EquivBugTransition } from "@archipad-js/archipad-model";


export class BugNoteFacade {
    constructor(
        private readonly workflowUserManager: WorkflowUserManager,
        private readonly workflowService: WorkflowService,
    ) { }

    createBugNote(bug: BugEntity, visit: VisitEntity | null): BugNoteEntity {
        const bugNote = this.createBugNoteModel(bug);
        try {
            bugNote.creationDate = new Date();
            bugNote.text = null;
            bugNote.visit = visit;
            bugNote.date = bugNote.visit?.date || new Date();
            bugNote.user = this.workflowUserManager.getCurrentUser(bug.project);
            bugNote.bugState = null;
            return bugNote;
        }
        catch (ex) {
            bugNote.getContext().deleteEntity(bugNote);
            throw ex;
        }
    }

    upgradeBugNotes(project: ProjectEntity): void {
        const entityContext = project.getContext();
        const queryBug = query(entityContext, "Bug");
        const resultSet = queryBug.execute({});
        resultSet.forEach((bug: BugEntity) => {
            this._convertToBugNotes(bug);
        }, this);
    }

    createBugNoteModelPrimitive(bug: BugEntity, deterministicId?): BugNoteEntity {
        const entityContext = bug.getContext();
        const bugNote = entityContext.createEntity("BugNote", deterministicId);
        bugNote.bug = bug;
        bugNote.creationDate = new Date();
        return bugNote;
    }

    createBugNoteModel(bug: BugEntity): BugNoteEntity {
        if (bug.notes.length == 0) {
            this._convertToBugNotes(bug);
        }
        return this.createBugNoteModelPrimitive(bug);
    }

    private _convertToBugNotes(bug: BugEntity) {
        if (bug.notes.length > 1) {
            return;
        }

        const project = bug.project;

        // optional entity, mapped for : bug created
        if (bug.notes.length == 0) {
            const bugNoteId = generateDeterministicId(bug.bugId, "1AB0DC21BB2D4EC28B5DFDC82898AC89", "uuid");
            const bugNote = this.createBugNoteModelPrimitive(bug, bugNoteId);
            bugNote.creationDate = bug.openDate;
            bugNote.text = null;
            bugNote.visit = bug.visit;
            bugNote.date = bug.openDate || bug.visit?.date;
            bugNote.user = null;
            bugNote.bugState = this.workflowService.findEquivBugState(EquivBugState.EquivStateOpen, project);
            bugNote.bugTransition = this.workflowService.findEquivTransition(EquivBugTransition.EquivTransitionEnter, bugNote.bugState, project);
        }

        // optional entity, mapped for : bug solved
        if (bug.notes.length == 1 && bug.closeDate != null) {
            const bugNoteId = generateDeterministicId(bug.bugId, "887329BD114949CCB2D0CBCC31B3F344", "uuid");
            const bugNote = this.createBugNoteModelPrimitive(bug, bugNoteId);
            const newDate = new Date(bug.closeDate.getTime() + 1000 * 60);
            bugNote.creationDate = newDate;
            bugNote.text = null;
            bugNote.visit = bug.closeVisit;
            bugNote.date = bug.closeDate ?? bug.closeVisit?.date;
            bugNote.user = null;
            bugNote.bugState = this.workflowService.findEquivBugState(EquivBugState.EquivStateClosed, project);
            bugNote.bugTransition = this.workflowService.findEquivTransition(EquivBugTransition.EquivTransitionAccept, bugNote.bugState, project);
        }
    }
}

export default new BugNoteFacade(
    _WorkflowUserManager,
    new WorkflowService(),
);